/* eslint-disable @typescript-eslint/no-explicit-any */
import {AfterViewInit, Component, ElementRef, Input, NgZone, OnChanges, OnDestroy, Output, Renderer2, SimpleChange} from '@angular/core';
import Plyr from 'plyr';
import {BehaviorSubject, Observable, Subscription} from 'rxjs';
import {filter, first, switchMap} from 'rxjs/operators';

@Component({
  selector: 'plyr, [plyr]',
  template: '',
  exportAs: 'plyr',
})
export class PlyrPlayerComponent implements AfterViewInit, OnChanges, OnDestroy {
  private playerChange = new BehaviorSubject<Plyr | null>(null);
  private events = new Map<string, (data: any) => void>();
  private subscriptions: Subscription[] = [];
  private videoElement!: HTMLVideoElement;

  @Input() plyrOptions?: Plyr.Options;
  @Input() plyrType: Plyr.MediaType = 'video';
  @Input() plyrTitle?: string;
  @Input() plyrPoster?: string;
  @Input() plyrSources?: Plyr.Source[];
  @Input() plyrTracks?: Plyr.Track[];
  @Input() plyrCrossOrigin?: boolean;
  @Input() plyrPlaysInline?: boolean;

  // events
  @Output() plyrInit = this.playerChange.pipe(filter((p): p is Plyr => !!p));
  @Output() plyrReady = this.createLazyEvent('ready');
  @Output() plyrPlaying = this.createLazyEvent('playing');
  @Output() plyrPlay = this.createLazyEvent('play');
  @Output() plyrPause = this.createLazyEvent('pause');
  @Output() plyrEnded = this.createLazyEvent('ended');
  @Output() plyrProgress = this.createLazyEvent('progress');
  @Output() plyrTimeUpdate = this.createLazyEvent('timeupdate');
  @Output() plyrError = this.createLazyEvent('error');

  get player(): Plyr {
    return this.playerChange.getValue() as Plyr;
  }

  constructor(
    private readonly elementRef: ElementRef<HTMLDivElement>,
    private readonly ngZone: NgZone,
    private readonly renderer: Renderer2,
  ) {}

  ngOnChanges(changes: {[p in keyof PlyrPlayerComponent]?: SimpleChange}) {
    this.subscriptions.push(
      this.plyrInit.pipe(first()).subscribe((player: Plyr) => {
        const reinitTriggers = [changes.plyrOptions, changes.plyrPlaysInline, changes.plyrCrossOrigin].filter((t) => !!t);

        if (reinitTriggers.length) {
          if (reinitTriggers.some((t) => !t!.firstChange)) {
            this.initPlyr(true);
          }
        } else {
          this.updatePlyrSource(player);
        }
      }),
    );
  }

  ngAfterViewInit() {
    this.initPlyr();
  }

  ngOnDestroy() {
    this.destroyPlayer();
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  private initPlyr(force = false) {
    if (force || !this.player) {
      this.ngZone.runOutsideAngular(() => {
        this.destroyPlayer();
        this.ensureVideoElement();
        const newPlayer = new Plyr(this.videoElement, this.plyrOptions);
        this.updatePlyrSource(newPlayer);
        this.playerChange.next(newPlayer);
      });
    }
  }

  private updatePlyrSource(plyr: Plyr) {
    if (this.plyrSources && this.plyrSources.length) {
      plyr.source = {
        type: this.plyrType,
        title: this.plyrTitle,
        sources: this.plyrSources,
        poster: this.plyrPoster,
        tracks: this.plyrTracks,
      };
    }
  }

  // see https://stackoverflow.com/a/53704102/1990451
  private createLazyEvent<T extends Plyr.PlyrEvent>(name: string): Observable<T> {
    return this.plyrInit.pipe(
      switchMap(() => new Observable<T>((observer) => this.on(name, (data: T) => this.ngZone.run(() => observer.next(data))))),
    );
  }

  private destroyPlayer() {
    if (this.player) {
      Array.from(this.events.keys()).forEach((name) => this.off(name));
      this.player.destroy();
      (this as any).videoElement = null;
    }
  }

  private get hostElement(): HTMLDivElement {
    return this.elementRef.nativeElement;
  }

  // Plyr inserts a clone of the original <video> on destroy — reuse it to avoid double-init bugs
  private ensureVideoElement() {
    const existing = this.hostElement.querySelector('video') as HTMLVideoElement | null;
    if (existing) {
      this.videoElement = existing;
    } else {
      this.videoElement = this.renderer.createElement('video');
      this.videoElement.controls = true;
      if (this.plyrCrossOrigin) {
        this.videoElement.setAttribute('crossorigin', '');
      }
      if (this.plyrPlaysInline) {
        this.videoElement.setAttribute('playsinline', '');
      }
      this.renderer.appendChild(this.hostElement, this.videoElement);
    }
  }

  private on(name: string, handler: (data: any) => void) {
    this.events.set(name, handler);
    this.player.on(name as any, handler);
  }

  private off(name: string) {
    this.player.off(name as any, this.events.get(name)!);
    this.events.delete(name);
  }
}
